import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined,
  TagOutlined,
  UserOutlined
} from '@ant-design/icons';
import {kanbanAPI} from '@/api/endpoints/kanban.js';
import styles from '@/styles/pages/kanban/KanbanBoard.module.css';

const KanbanBoard = () => {
  const { uuid: projectUUID } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [draggedTask, setDraggedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assignee_uuid: '',
    labels: [],
    due_date: '',
    estimated_hours: ''
  });

  // Приоритеты задач
  const priorities = {
    low: { label: 'Низкий', color: '#7d8590' },
    medium: { label: 'Средний', color: '#d29922' },
    high: { label: 'Высокий', color: '#f85149' },
    urgent: { label: 'Срочный', color: '#be5e00' }
  };

  // Загрузка данных
  const fetchKanban = useCallback(async () => {
    setLoading(true);
    const response = await kanbanAPI.get(projectUUID, false);
    if (response.success) {
      setBoard(response.data.board);
      setColumns(response.data.columns || []);
    }
    setLoading(false);
  }, [projectUUID]);

  useEffect(() => {
    fetchKanban();
  }, [fetchKanban]);

  // Обработка drag and drop
  const handleDragStart = (e, task, columnId) => {
    setDraggedTask({ ...task, sourceColumnId: columnId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetColumnId, targetOrder) => {
    e.preventDefault();
    if (!draggedTask) return;

    const { id, sourceColumnId, order } = draggedTask;

    if (sourceColumnId === targetColumnId && order === targetOrder) {
      setDraggedTask(null);
      return;
    }

    // Оптимистичное обновление
    const newColumns = [...columns];
    const sourceColumn = newColumns.find(c => c.id === sourceColumnId);
    const targetColumn = newColumns.find(c => c.id === targetColumnId);

    // Удаляем задачу из исходной колонки
    const taskIndex = sourceColumn.tasks.findIndex(t => t.id === id);
    const [movedTask] = sourceColumn.tasks.splice(taskIndex, 1);

    // Вставляем в целевую колонку
    let insertIndex = targetColumn.tasks.findIndex(t => t.order === targetOrder);
    if (insertIndex === -1) insertIndex = targetColumn.tasks.length;
    targetColumn.tasks.splice(insertIndex, 0, movedTask);

    // Обновляем порядок задач
    sourceColumn.tasks.forEach((task, idx) => { task.order = idx; });
    targetColumn.tasks.forEach((task, idx) => { task.order = idx; });

    setColumns(newColumns);

    // Отправляем запрос на сервер
    const response = await kanbanAPI.move(projectUUID, id, {
      column_id: targetColumnId,
      new_order: targetOrder
    });

    if (!response.success) {
      // Откат при ошибке
      fetchKanban();
    }

    setDraggedTask(null);
  };

  // Создание/обновление задачи
  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      alert('Введите название задачи');
      return;
    }

    let response;
    if (editingTask) {
      response = await kanbanAPI.patch(projectUUID, editingTask.id, taskForm, false);
    } else {
      response = await kanbanAPI.post(projectUUID, {
        ...taskForm,
        column_id: columns[0]?.id
      });
    }

    if (response.success) {
      setShowTaskModal(false);
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        assignee_uuid: '',
        labels: [],
        due_date: '',
        estimated_hours: ''
      });
      fetchKanban();
    }
  };

  // Удаление задачи
  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Вы уверены, что хотите удалить эту задачу?')) {
      const response = await kanbanAPI.delete(projectUUID, taskId);
      if (response.success) {
        fetchKanban();
      }
    }
  };

  // Открыть модальное окно редактирования
  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      assignee_uuid: task.assignee_uuid || '',
      labels: task.labels || [],
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      estimated_hours: task.estimated_hours || ''
    });
    setShowTaskModal(true);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingOutlined className={styles.loadingSpinner} />
        <p>Загрузка канбан-доски...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.bgGradient}></div>
      <div className={styles.bgGrid}></div>

      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowLeftOutlined /> Назад
          </button>
          <h1 className={styles.title}>
            {board?.name || 'Канбан-доска'}
          </h1>
          <button
            className={styles.addTaskButton}
            onClick={() => {
              setEditingTask(null);
              setTaskForm({
                title: '',
                description: '',
                priority: 'medium',
                assignee_uuid: '',
                labels: [],
                due_date: '',
                estimated_hours: ''
              });
              setShowTaskModal(true);
            }}
          >
            <PlusOutlined /> Новая задача
          </button>
        </div>

        {/* Канбан-доска */}
        <div className={styles.kanbanBoard}>
          {columns.map(column => (
            <div
              key={column.id}
              className={styles.column}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id, column.tasks.length)}
            >
              <div className={styles.columnHeader}>
                <h3 className={styles.columnTitle}>
                  {column.name}
                  {column.wip_limit && (
                    <span className={styles.wipLimit}>
                      {column.tasks.length}/{column.wip_limit}
                    </span>
                  )}
                </h3>
                <span className={styles.taskCount}>{column.tasks.length}</span>
              </div>

              <div className={styles.tasksList}>
                {column.tasks.map((task, index) => (
                  <div
                    key={task.id}
                    className={styles.taskCard}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task, column.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, column.id, task.order)}
                  >
                    <div className={styles.taskHeader}>
                      <div
                        className={styles.priorityDot}
                        style={{ backgroundColor: priorities[task.priority]?.color }}
                      />
                      <button
                        className={styles.taskMenuButton}
                        onClick={() => handleEditTask(task)}
                      >
                        <EditOutlined />
                      </button>
                      <button
                        className={styles.taskDeleteButton}
                        onClick={() => handleDeleteTask(task.id)}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>

                    <h4 className={styles.taskTitle}>{task.title}</h4>

                    {task.description && (
                      <p className={styles.taskDescription}>
                        {task.description.length > 100
                          ? `${task.description.substring(0, 100)}...`
                          : task.description}
                      </p>
                    )}

                    <div className={styles.taskMeta}>
                      {task.priority && (
                        <span
                          className={styles.priorityBadge}
                          style={{ backgroundColor: priorities[task.priority]?.color + '20', color: priorities[task.priority]?.color }}
                        >
                          {priorities[task.priority]?.label}
                        </span>
                      )}

                      {task.due_date && (
                        <span className={styles.dueDate}>
                          <ClockCircleOutlined />
                          {new Date(task.due_date).toLocaleDateString('ru-RU')}
                        </span>
                      )}
                    </div>

                    {task.labels && task.labels.length > 0 && (
                      <div className={styles.labels}>
                        {task.labels.slice(0, 3).map((label, idx) => (
                          <span key={idx} className={styles.label}>
                            <TagOutlined /> {label}
                          </span>
                        ))}
                        {task.labels.length > 3 && (
                          <span className={styles.label}>+{task.labels.length - 3}</span>
                        )}
                      </div>
                    )}

                    {(task.assignee_uuid || task.subtasks?.length > 0) && (
                      <div className={styles.taskFooter}>
                        {task.assignee_uuid && (
                          <span className={styles.assignee}>
                            <UserOutlined /> {task.assignee_uuid}
                          </span>
                        )}
                        {task.subtasks?.length > 0 && (
                          <span className={styles.subtaskCount}>
                            {task.subtasks.filter(st => st.is_completed).length}/{task.subtasks.length}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {column.tasks.length === 0 && (
                  <div className={styles.emptyColumn}>
                    <p>Нет задач</p>
                    <button
                      className={styles.addTaskToColumn}
                      onClick={() => {
                        setEditingTask(null);
                        setTaskForm({
                          title: '',
                          description: '',
                          priority: 'medium',
                          assignee_uuid: '',
                          labels: [],
                          due_date: '',
                          estimated_hours: ''
                        });
                        setShowTaskModal(true);
                      }}
                    >
                      <PlusOutlined /> Добавить задачу
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Модальное окно задачи */}
      {showTaskModal && (
        <div className={styles.modalOverlay} onClick={() => setShowTaskModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingTask ? 'Редактирование задачи' : 'Новая задача'}</h2>
              <button className={styles.modalClose} onClick={() => setShowTaskModal(false)}>
                <CloseOutlined />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Название задачи *</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Введите название задачи"
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Описание</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Опишите задачу"
                  className={styles.textarea}
                  rows={4}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Приоритет</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className={styles.select}
                  >
                    <option value="low">Низкий</option>
                    <option value="medium">Средний</option>
                    <option value="high">Высокий</option>
                    <option value="urgent">Срочный</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Срок выполнения</label>
                  <input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Оценка времени (часы)</label>
                  <input
                    type="number"
                    value={taskForm.estimated_hours}
                    onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: e.target.value })}
                    className={styles.input}
                    step="0.5"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Метки (через запятую)</label>
                  <input
                    type="text"
                    value={taskForm.labels.join(', ')}
                    onChange={(e) => setTaskForm({ ...taskForm, labels: e.target.value.split(',').map(l => l.trim()).filter(l => l) })}
                    placeholder="React, Frontend, Bug"
                    className={styles.input}
                  />
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setShowTaskModal(false)}>
                Отмена
              </button>
              <button className={styles.saveButton} onClick={handleSaveTask}>
                <CheckOutlined /> {editingTask ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;